// Production kits (APS, docs/design/aps.md §3–§11) — data access.
//
//   list     rpc production_kit_page(company, status, q, after_start, after_id, limit 51)
//            keyset (planned_start, id) on production_kits_list_idx; ATP columns from the
//            production_kit_atp snapshot (one PK probe per row).
//   detail   rpc production_kit_document(id) — header, orders with tree links and coverage,
//            live ATP / CTP (app.kit_atp), hard-peg violations, MRP conflicts; one round-trip.
//   writes   create_production_kit / create_kit_from_order / set_kit_orders /
//            set_kit_order_parent / shift_production_kit / move_production_order /
//            check_kit_atp / close_production_kit — each one transaction, each returns the kit.

import type { KitAtp, KitAtpStatus, KitDocument, KitRow, MoveOrderResult, ShiftKitResult } from "@patronix/domain";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import type { StatusTone } from "@/components/ui";
import { isDemoMode, supabase } from "@/lib/supabase";

export type KitStatusFilter = "open" | "closed" | "all";
export const kitStatusFilters: KitStatusFilter[] = ["open", "closed", "all"];
export const isKitStatusFilter = (v: unknown): v is KitStatusFilter => v === "open" || v === "closed" || v === "all";

export const atpTone: Record<KitAtpStatus, StatusTone> = {
  full_ok: "success",
  partial_shortage: "warning",
  critical_lock: "danger",
};

export interface KitListParams {
  companyId: string;
  status: KitStatusFilter;
  q: string;
}

export const KIT_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listKits(p: KitListParams, cursor: string | null, signal: AbortSignal, size = KIT_PAGE): Promise<Page<KitRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const after = cursor ? (JSON.parse(cursor) as { start: string; id: string }) : null;
  const { data, error } = await db()
    .rpc("production_kit_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_q: p.q.trim() || null,
      p_after_start: after?.start ?? null,
      p_after_id: after?.id ?? null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as KitRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify({ start: last.planned_start, id: last.id }) : null };
}

async function getKit(id: string, signal: AbortSignal): Promise<KitDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("production_kit_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as KitDocument | null) ?? null;
}

// Shapes: @patronix/domain aps.ts (the Zod schemas are the contract; the
// browser casts to keep zod out of the route chunk, as the other features).
async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  if (isDemoMode) throw new Error("Kits are not available in demo mode");
  const { data, error } = await db().rpc(fn, args);
  if (error) throw error;
  return data as T;
}

export const createKit = (companyId: string, name: string, orderIds: string[], note?: string) =>
  call<KitDocument>("create_production_kit", { p_company_id: companyId, p_name: name, p_order_ids: orderIds, p_note: note ?? null });

export const createKitFromOrder = (orderId: string, name?: string) =>
  call<KitDocument>("create_kit_from_order", { p_order_id: orderId, p_name: name ?? null });

export const setKitOrders = (kit: Pick<KitDocument, "id" | "revision">, add: string[], remove: string[], name?: string) =>
  call<KitDocument>(
    "set_kit_orders",
    { p_kit_id: kit.id, p_add: add, p_remove: remove, p_expected_revision: kit.revision, p_name: name ?? null }
  );

export const setKitOrderParent = (kit: Pick<KitDocument, "revision">, orderId: string, parentId: string | null, offsetDays: number) =>
  call<KitDocument>(
    "set_kit_order_parent",
    { p_order_id: orderId, p_parent_order_id: parentId, p_lead_time_offset_days: offsetDays, p_expected_revision: kit.revision }
  );

export const shiftKit = (kit: Pick<KitDocument, "id" | "revision">, newStart: string): Promise<ShiftKitResult> =>
  call<ShiftKitResult>("shift_production_kit", { p_kit_id: kit.id, p_new_start: newStart, p_expected_revision: kit.revision });

export const moveOrder = (orderId: string, revision: number, newStart: string, confirm = false): Promise<MoveOrderResult> =>
  call<MoveOrderResult>(
    "move_production_order",
    { p_order_id: orderId, p_new_start: newStart, p_expected_revision: revision, p_confirm: confirm }
  );

export const checkKitAtp = (kitId: string): Promise<KitAtp> => call<KitAtp>("check_kit_atp", { p_kit_id: kitId });

export const closeKit = (kitId: string) => call<KitDocument>("close_production_kit", { p_kit_id: kitId });

export const kitKeys = {
  all: ["production", "kits"] as const,
  list: (p: KitListParams) => [...kitKeys.all, "list", p] as const,
  detail: (id: string) => [...kitKeys.all, "detail", id] as const,
};

export function kitListQuery(p: KitListParams) {
  return infiniteQueryOptions({
    queryKey: kitKeys.list(p),
    queryFn: ({ pageParam, signal }) => listKits(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

// Coverage moves with every posting elsewhere: short staleTime, background refetch.
export function kitDetailQuery(id: string) {
  return queryOptions({ queryKey: kitKeys.detail(id), queryFn: ({ signal }) => getKit(id, signal), staleTime: 15_000 });
}

const rules: [RegExp, string][] = [
  [/was changed by someone else|changed meanwhile/, "kit.err.stale"],
  [/^Release lock/, "kit.err.releaseLock"],
  [/form a cycle/, "kit.err.cycle"],
  [/^The parent must be another order of the same kit/, "kit.err.parent"],
  [/is already in kit/, "kit.err.inKit"],
  [/^A kit holds/, "kit.err.size"],
  [/^Name: 1 to 120/, "kit.err.name"],
  [/^Date: within a year back/, "kit.err.date"],
  [/^Only planned or released orders move/, "kit.err.notMovable"],
  [/^A kit closes when none/, "kit.err.closeOpen"],
  [/^The kit is (already )?closed/, "kit.err.closed"],
  [/^Missing permission/, "prd.err.permission"],
];

export function kitErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, vars?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1], { message }) : message || tr("auth.errorGeneric");
}
